import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DO_MOTIVO,
  bN,
  diaCurto,
  hojeBrasilia,
  iniciais,
  lerEquipe,
  lerExcecoesDoGiro,
  lerHistoricoDoGiro,
  lerRodizio,
  lerSetores,
  salvarPresencas,
} from '../../giro';
import type {
  EquipeDoGiro,
  ExcecoesDoGiro,
  HistoricoDoGiro,
  MatrizDoRodizio,
  MotivoForaDaMissao,
  SetorDoGiro,
} from '../../giro';
import { diaDeBrasilia } from '../../giro-gestor';
import { hora } from '../../vendas';
import { CalendarioDeFolgas } from './CalendarioDeFolgas';
import { Vinculos } from './Geracao';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * TELA 18 — EQUIPE, RODÍZIO E DADOS. "Vínculo não é presença. Check é conclusão."
 *
 * Presença é de um toque e é do dia; o vínculo ao setor é permanente. A matriz de rodízio só marca
 * ✓ onde o bloco foi CONCLUÍDO; ausência não consome nada; transferência não credita o bloco a
 * ninguém; 7 pessoas em 6 blocos não criam um B7. "Próximos elegíveis", nunca "próximo bloco".
 */

type Estado = 'PRESENTE' | 'PARCIAL' | 'AUSENTE';

function useSetores() {
  const [setores, setSetores] = useState<readonly SetorDoGiro[] | null>(null);
  useEffect(() => {
    void lerSetores().then((r) => r.ok && setSetores(r.dados.setores.filter((s) => s.ativo)));
  }, []);
  return setores;
}

export function EquipeEPresenca({
  sessao,
  parametros,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
}) {
  const dia = hojeBrasilia();
  const setores = useSetores();
  const [setor, setSetor] = useState<string | null>(parametros.get('setor'));
  const [d, setD] = useState<EquipeDoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const [mudancas, setMudancas] = useState<Map<string, Estado>>(new Map());
  const [busca, setBusca] = useState('');
  const [falha, setFalha] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const setorId = setor ?? setores?.[0]?.id ?? null;
  const carregar = useCallback(() => {
    if (!setorId) return;
    setErro(false);
    void lerEquipe(dia, setorId).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [dia, setorId]);
  useEffect(carregar, [carregar]);

  const estadoDe = (id: string, atual: Estado) => mudancas.get(id) ?? atual;
  const pessoas = (d?.pessoas ?? []).filter(
    (p) => !busca || p.nome.toLowerCase().includes(busca.toLowerCase()),
  );
  // Sem mudança local, os totais são os do servidor; com mudança, o servidor + o que mudou na tela.
  const conta = (e: Estado) => {
    const base = !d
      ? 0
      : e === 'AUSENTE'
        ? d.ausentes
        : e === 'PARCIAL'
          ? d.parciais
          : d.presentes - d.parciais;
    let delta = 0;
    for (const p of d?.pessoas ?? []) {
      const novo = mudancas.get(p.accountId);
      if (!novo || novo === p.presenca) continue;
      if (novo === e) delta += 1;
      if (p.presenca === e) delta -= 1;
    }
    return base + delta;
  };
  const presentes = conta('PRESENTE') + conta('PARCIAL');
  const semBloco = (d?.pessoas ?? []).filter(
    (p) => estadoDe(p.accountId, p.presenca) !== 'AUSENTE' && !p.blocosHoje.length,
  );

  const salvar = () => {
    if (!mudancas.size || salvando) return;
    setFalha(null);
    setSalvo(false);
    setSalvando(true);
    void salvarPresencas(
      sessao,
      dia,
      [...mudancas].map(([accountId, estado]) => ({ accountId, estado })),
    ).then((r) => {
      setSalvando(false);
      if (r.tipo === 'confirmada') {
        setMudancas(new Map());
        setSalvo(true);
        carregar();
      } else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else
        setFalha(
          'Sem resposta do servidor: a presença pode ter sido salva. Recarregue antes de repetir.',
        );
    });
  };

  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-equipe" direita={`presença de ${diaCurto(dia)}`} />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>Equipe &amp; presença</h1>
            <span>
              {d
                ? `${String(d.vinculados)} vinculados · ${String(presentes)} presentes hoje · vínculo ao setor é permanente, presença é do dia`
                : ' '}
            </span>
          </div>
          <div className="cv-topo-dir">
            <label className="cv-busca gr-busca-peq">
              <Icone nome="search" tamanho={14} traco={1.7} />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Pessoa"
                aria-label="Buscar pessoa"
              />
            </label>
            <label className="cv-select">
              Setor
              <select value={setorId ?? ''} onChange={(e) => setSetor(e.target.value)}>
                {setores?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="cv-botao-primario"
              disabled={!mudancas.size || salvando}
              onClick={salvar}
            >
              Salvar presença
            </button>
          </div>
        </div>
        {falha && (
          <p className="cv-falha" role="alert">
            {falha}
          </p>
        )}
        {salvo && (
          <p className="gr-ok-txt">
            Presença salva. Vale para a próxima geração; a operação publicada não muda sozinha.
          </p>
        )}
        {setores && !setores.length ? (
          <div className="cv-cartao gr-vazio-grande">
            <h2>Nenhum setor configurado</h2>
            <a className="cv-botao-primario gr-botao-link" href="#/giro-setores">
              Configurar setores
            </a>
          </div>
        ) : erro ? (
          <ErroDeArea oQue="a equipe" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="cv-kpis cv-kpis-4">
              <div className="cv-kpi">
                <div className="cv-kpi-rotulo">
                  PRESENTES
                  <Ajuda rotulo="presentes">
                    Quem está na loja hoje e pode receber bloco, do total de pessoas ligadas a este
                    setor.
                  </Ajuda>
                </div>
                <div className="cv-kpi-valor gr-verde">{presentes}</div>
                <div className="cv-kpi-sub">de {d.vinculados} vinculados</div>
              </div>
              <div className="cv-kpi">
                <div className="cv-kpi-rotulo">
                  AUSENTES
                  <Ajuda rotulo="ausentes">
                    Quem faltou ou está de folga hoje. Não recebe bloco; o bloco é dividido entre
                    quem veio.
                  </Ajuda>
                </div>
                <div className="cv-kpi-valor">{conta('AUSENTE')}</div>
                <div className="cv-kpi-sub">não recebem bloco</div>
              </div>
              <div className="cv-kpi">
                <div className="cv-kpi-rotulo">
                  PARCIAL
                  <Ajuda rotulo="parcial">
                    Quem trabalha só meio período hoje. Recebe um bloco menor (metade da carga).
                  </Ajuda>
                </div>
                <div className="cv-kpi-valor gr-ambar">{conta('PARCIAL')}</div>
                <div className="cv-kpi-sub">capacidade 0,5</div>
              </div>
              <div className="cv-kpi">
                <div className="cv-kpi-rotulo">
                  SEM BLOCO HOJE
                  <Ajuda rotulo="sem bloco hoje">
                    Pessoas presentes que ficaram sem bloco porque há menos blocos do que gente.
                  </Ajuda>
                </div>
                <div className="cv-kpi-valor">{semBloco.length}</div>
                <div className="cv-kpi-sub">
                  {presentes} presentes / {d.blocosComCarga} blocos
                </div>
              </div>
            </div>
            <div className="cv-cartao cv-sem-pad">
              <div className="cv-rolagem">
                <table className="cv-tabela gr-tabela-equipe">
                  <thead>
                    <tr>
                      <th>Pessoa</th>
                      <th>Setores vinculados</th>
                      <th>Presença de hoje</th>
                      <th className="cv-dir">
                        Peso
                        <Ajuda rotulo="peso">
                          Quanto da carga a pessoa recebe: 1,0 = bloco inteiro, 0,5 = meio bloco
                          (meio período).
                        </Ajuda>
                      </th>
                      <th>Blocos hoje</th>
                      <th className="cv-dir">
                        Carga
                        <Ajuda rotulo="carga">Quantos produtos a pessoa tem hoje no Giro.</Ajuda>
                      </th>
                      <th className="cv-dir">
                        Ciclo
                        <Ajuda rotulo="ciclo">
                          Quantos blocos do setor a pessoa já terminou no rodízio atual, do total.
                          Quando passa por todos, começa um ciclo novo.
                        </Ajuda>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pessoas.map((p) => {
                      const e = estadoDe(p.accountId, p.presenca);
                      return (
                        <tr key={p.accountId} className={e === 'AUSENTE' ? 'gr-linha-fora' : ''}>
                          <td>
                            <div className="gr-pessoa gr-pessoa-linha">
                              <span
                                className={`gr-avatar ${e === 'AUSENTE' ? 'gr-avatar-fora' : ''}`}
                              >
                                {iniciais(p.nome)}
                              </span>
                              <div>
                                <b>{p.nome}</b>
                                <em>{p.perfil}</em>
                              </div>
                            </div>
                          </td>
                          <td className="cv-cinza">{p.setores.map((s) => s.nome).join(' · ')}</td>
                          <td>
                            <div
                              className="gr-presenca"
                              role="radiogroup"
                              aria-label={`Presença de ${p.nome}`}
                            >
                              {(['PRESENTE', 'PARCIAL', 'AUSENTE'] as const).map((op) => (
                                <button
                                  key={op}
                                  type="button"
                                  role="radio"
                                  aria-checked={e === op}
                                  className={e === op ? `gr-pres-${op}` : ''}
                                  onClick={() =>
                                    setMudancas((m) => {
                                      const n = new Map(m);
                                      if (op === p.presenca && p.presencaRegistrada)
                                        n.delete(p.accountId);
                                      else n.set(p.accountId, op);
                                      return n;
                                    })
                                  }
                                >
                                  {op === 'PRESENTE'
                                    ? 'Presente'
                                    : op === 'PARCIAL'
                                      ? 'Parcial'
                                      : 'Ausente'}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className={`cv-dir ${e === 'PARCIAL' ? 'gr-ambar cv-forte' : ''}`}>
                            {e === 'AUSENTE' ? '—' : e === 'PARCIAL' ? '0,5' : '1,0'}
                          </td>
                          <td>
                            {e === 'AUSENTE' && !p.blocosHoje.length ? (
                              <span className="gr-vermelho cv-peq">
                                não atribuída · rodízio não consumido
                              </span>
                            ) : p.blocosHoje.length ? (
                              <span className="gr-chips-etapa">
                                {p.blocosHoje.map((b) => (
                                  <span
                                    key={`${b.setor}${String(b.numero)}`}
                                    className={`gr-chip gr-chip-bloco ${b.recebido ? 'gr-chip-azul' : ''}`}
                                  >
                                    {bN(b.numero)}
                                    {b.recebido ? ' recebido' : ''}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="gr-chip gr-chip-and">sem bloco · disponível</span>
                            )}
                          </td>
                          <td className="cv-dir">
                            {p.pendentesRecebidos
                              ? `${String(p.pendentesRecebidos)} pend.`
                              : p.carga
                                ? `${String(p.carga)} SKUs`
                                : '—'}
                          </td>
                          <td className="cv-dir cv-forte">
                            {p.ciclo ? `${String(p.ciclo.feitos)} / ${String(p.ciclo.total)}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {semBloco.length > 0 && d.blocosComCarga > 0 && (
                <div className="gr-rodape-equipe">
                  <p>
                    <b>
                      {presentes} presentes para {d.blocosComCarga} blocos:
                    </b>{' '}
                    {semBloco.map((p) => p.nome).join(', ')} fica(m) disponível(is) para outro setor
                    ou apoio. A próxima distribuição prioriza quem está há mais tempo de fora.
                  </p>
                </div>
              )}
              {!d.pessoas.length && (
                <p className="cv-vazio">
                  Ninguém vinculado a este setor ainda. Vincule logo abaixo.
                </p>
              )}
            </div>
            {setorId && (
              <div className="cv-cartao gr-vinculos-equipe">
                <Vinculos key={setorId} sessao={sessao} setorId={setorId} aoMudar={carregar} />
              </div>
            )}
            <CalendarioDeFolgas sessao={sessao} aoMudar={carregar} />
            {setorId && (
              <MatrizDeRodizio setorId={setorId} setores={setores ?? []} aoTrocar={setSetor} />
            )}
          </>
        )}
      </div>
    </section>
  );
}

export function MatrizDeRodizio({
  setorId,
  setores,
  aoTrocar,
}: {
  setorId: string;
  setores: readonly SetorDoGiro[];
  aoTrocar: (id: string) => void;
}) {
  const [m, setM] = useState<MatrizDoRodizio | null>(null);
  useEffect(() => {
    setM(null);
    void lerRodizio(setorId).then((r) => r.ok && setM(r.dados));
  }, [setorId]);
  const nome = setores.find((s) => s.id === setorId)?.nome ?? '';
  return (
    <div className="cv-cartao gr-matriz">
      <div className="cv-cartao-topo">
        <div>
          <h2>
            Matriz de rodízio · {nome}
            <Ajuda rotulo="a matriz de rodízio">
              Cada linha é uma pessoa e cada coluna um bloco. ✓ = já fez aquele bloco inteiro no
              ciclo; &quot;hoje&quot; = está com ele agora. Próximos elegíveis = blocos que ela
              ainda não fez e pode receber.
            </Ajuda>
          </h2>
          <p className="cv-cinza cv-peq">
            {m?.cicloDesde
              ? `Ciclo atual iniciado em ${diaCurto(diaDeBrasilia(m.cicloDesde))} · `
              : ''}
            ✓ marca bloco concluído, nunca apenas atribuído
          </p>
        </div>
        <div className="cv-mini-seg">
          {/* todos os setores ativos (eram só os 4 primeiros: Suplemento e Ético sumiam) */}
          {setores.map((s) => (
            <button
              key={s.id}
              type="button"
              className={s.id === setorId ? 'cv-sel' : ''}
              onClick={() => aoTrocar(s.id)}
            >
              {s.nome}
            </button>
          ))}
        </div>
      </div>
      {!m ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : (
        <div className="cv-rolagem">
          <table className="cv-tabela gr-tabela-matriz">
            <thead>
              <tr>
                <th>Pessoa</th>
                {Array.from({ length: m.blocos }, (_, i) => (
                  <th key={i} className="gr-centro">
                    {bN(i + 1)}
                  </th>
                ))}
                <th className="cv-dir">Ciclo</th>
                <th>Próximos elegíveis</th>
              </tr>
            </thead>
            <tbody>
              {m.linhas.map((l) => (
                <tr key={l.accountId}>
                  <td className={l.ausente ? 'cv-cinza' : 'cv-forte'}>
                    {l.nome}
                    {l.ausente && <span className="cv-peq"> ausente</span>}
                  </td>
                  {l.celulas.map((c, i) => (
                    <td key={i} className="gr-centro">
                      {c === 'concluido' ? (
                        <Icone nome="check" tamanho={14} traco={2.2} />
                      ) : c === 'hoje' ? (
                        <span className="cv-mono cv-cinza cv-peq">hoje</span>
                      ) : c === 'parcial' ? (
                        <span className="gr-chip gr-chip-azul">parcial</span>
                      ) : (
                        <span className="cv-cinza">—</span>
                      )}
                    </td>
                  ))}
                  <td className="cv-dir cv-forte">
                    {l.feitos} / {l.total}
                  </td>
                  <td className="cv-cinza">{l.proximos.map(bN).join(', ') || 'volta nova'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!m.linhas.length && <p className="cv-vazio">Ninguém vinculado.</p>}
        </div>
      )}
      <div className="gr-legenda">
        <span>
          <Icone nome="check" tamanho={12} traco={2.2} /> concluído no ciclo
        </span>
        <span>
          <span className="gr-chip gr-chip-azul">parcial</span> transferido, sem conclusão
        </span>
        <span>
          <span className="cv-mono">hoje</span> em andamento nesta operação
        </span>
        <span>— ainda não concluído</span>
      </div>
      <p className="cv-cinza cv-peq gr-dir">
        &quot;Próximos elegíveis&quot;, nunca &quot;próximo bloco&quot; — depende de presença e
        carga
      </p>
    </div>
  );
}

export function RodizioDoGiro() {
  const setores = useSetores();
  const [setor, setSetor] = useState<string | null>(null);
  const id = setor ?? setores?.[0]?.id ?? null;
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-rodizio" />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Rodízio</h1>
          <span>
            O ciclo é pessoal: cada um circula por todos os blocos do setor. Só a conclusão do bloco
            inteiro consome a vez.
          </span>
        </div>
        {id ? (
          <MatrizDeRodizio setorId={id} setores={setores ?? []} aoTrocar={setSetor} />
        ) : setores ? (
          <p className="cv-vazio">Nenhum setor configurado.</p>
        ) : (
          <div className="cv-carregando" />
        )}
      </div>
    </section>
  );
}

/* ================================================================== *
 * 18B — Exceções da geração e histórico
 * ================================================================== */

const CHIPS: readonly (MotivoForaDaMissao | 'EAN')[] = [
  'ESTOQUE_ZERO',
  'ESTOQUE_INDISPONIVEL',
  'ESTOQUE_STALE',
  'SEM_SETOR',
  'SEM_CADASTRO',
  'SETOR_SEM_EQUIPE',
  'EAN',
];

export function ExcecoesDaGeracao() {
  const dia = hojeBrasilia();
  const [d, setD] = useState<ExcecoesDoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState<MotivoForaDaMissao | 'EAN' | null>(null);
  const carregar = useCallback(() => {
    setErro(false);
    void lerExcecoesDoGiro(dia).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [dia]);
  useEffect(carregar, [carregar]);
  const total = d ? new Set(d.itens.map((i) => i.productId ?? i.codigo ?? i.nome)).size : 0;
  const linhas = d?.itens.filter((i) => !filtro || i.motivo === filtro) ?? [];
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-excecoes" excecoes={total} />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>
            Exceções da geração
            <Ajuda rotulo="as exceções">
              <b>Estoque zero</b>: o HOS diz que não tem — vira ruptura. <b>Estoque indisponível</b>
              ou <b>desatualizado</b>: sem leitura confiável do HOS, então fica fora (nunca vira
              zero). <b>Sem setor</b>: o grupo do produto não está em nenhum setor do Giro.{' '}
              <b>Sem cadastro</b>: vendeu um código que o InovaPay não conhece.
            </Ajuda>
          </h1>
          <span>
            {d
              ? `Operação de ${diaCurto(d.dia)} · vendas de ${diaCurto(d.vendasDe)} · estoque zero, dado indisponível, sem setor e sem cadastro são listas distintas`
              : ' '}
          </span>
        </div>
        {erro ? (
          <ErroDeArea oQue="as exceções" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="cv-chips">
              <button
                type="button"
                className={`cv-chip ${!filtro ? 'cv-chip-sel' : ''}`}
                onClick={() => setFiltro(null)}
              >
                Exceções · <b>{total}</b>
              </button>
              {CHIPS.filter((c) => (c === 'EAN' ? d.eansDesconhecidos.length : d.contagem[c])).map(
                (c) => (
                  <button
                    key={c}
                    type="button"
                    className={`cv-chip ${filtro === c ? 'cv-chip-sel' : ''} ${c === 'ESTOQUE_INDISPONIVEL' || c === 'ESTOQUE_STALE' ? 'gr-chip-ambar-borda' : ''}`}
                    onClick={() => setFiltro(c)}
                  >
                    {c === 'EAN' ? 'Código não reconhecido' : ROTULO_DO_MOTIVO[c]}{' '}
                    <b>{c === 'EAN' ? d.eansDesconhecidos.length : d.contagem[c]}</b>
                  </button>
                ),
              )}
              <span style={{ flex: 1 }} />
              <span className="cv-cinza cv-peq">
                {total} {total === 1 ? 'caso único' : 'casos únicos'} · um produto pode ter duas
                tags
              </span>
            </div>
            {filtro === 'EAN' ? (
              <div className="cv-cartao cv-sem-pad">
                <table className="cv-tabela">
                  <thead>
                    <tr>
                      <th>Código lido</th>
                      <th>Produto esperado</th>
                      <th>Quem leu</th>
                      <th>Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.eansDesconhecidos.map((e, k) => (
                      <tr key={k}>
                        <td className="cv-mono cv-forte">{e.ean}</td>
                        <td>{e.produtoEsperado ?? '—'}</td>
                        <td>{e.quem ?? '—'}</td>
                        <td className="cv-mono cv-cinza">{hora(e.em)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="cv-rodape">
                  <span>
                    O balconista nunca altera dado mestre — revisar o EAN é do administrador, no
                    cadastro.
                  </span>
                </div>
              </div>
            ) : (
              <div className="cv-cartao cv-sem-pad">
                {linhas.length === 0 ? (
                  <p className="cv-vazio">Nenhuma exceção na geração de hoje.</p>
                ) : (
                  <div className="cv-rolagem">
                    <table className="cv-tabela">
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th>Setor</th>
                          <th className="cv-dir">Vendido {diaCurto(d.vendasDe)}</th>
                          <th className="cv-dir">Estoque</th>
                          <th>Detalhe</th>
                          <th>Tag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((i, k) => (
                          <tr key={k}>
                            <td>
                              <div className="cv-forte">{i.nome ?? i.codigo ?? '—'}</div>
                              <div className="cv-mono cv-cinza cv-peq">
                                {i.productId ? (i.codigo ?? '—') : 'não localizado no catálogo'}
                              </div>
                            </td>
                            <td className={i.motivo === 'SEM_SETOR' ? 'gr-vermelho cv-forte' : ''}>
                              {i.motivo === 'SEM_SETOR' ? 'sem classificação' : (i.setor ?? '—')}
                            </td>
                            <td className="cv-dir">{String(i.vendido).replace('.', ',')} un</td>
                            <td
                              className={`cv-dir ${i.estoque === null ? 'gr-indisp' : 'cv-forte'}`}
                            >
                              {i.estoque === null
                                ? i.motivo === 'ESTOQUE_STALE'
                                  ? 'desatualizado'
                                  : i.motivo === 'SEM_CADASTRO'
                                    ? '—'
                                    : 'indisponível'
                                : Math.floor(i.estoque)}
                            </td>
                            <td className="cv-cinza cv-peq">{i.detalhe ?? ''}</td>
                            <td>
                              <span className={`gr-tag-motivo gr-tag-${i.motivo}`}>
                                {ROTULO_DO_MOTIVO[i.motivo]}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="cv-rodape">
                  <span>
                    Corrigir setor ou cadastro requer permissão de administrador — o balconista
                    nunca altera dado mestre.
                  </span>
                  <a className="gr-link" href="#/giro-setores">
                    Classificar grupos em setores
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

const ROTULO_DO_ESTADO: Record<HistoricoDoGiro['dias'][number]['estado'], string> = {
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  rascunho: 'Rascunho',
};

export function HistoricoDeOperacoes() {
  const [d, setD] = useState<HistoricoDoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerHistoricoDoGiro().then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, []);
  useEffect(carregar, [carregar]);
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-historico" />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Histórico de operações</h1>
          <span>
            Cada dia abre com a foto daquele dia — não recalculada com a configuração de hoje
          </span>
        </div>
        {erro ? (
          <ErroDeArea oQue="o histórico" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <div className="cv-cartao cv-sem-pad gr-historico">
            <table className="cv-tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="cv-dir">Setores</th>
                  <th className="cv-dir">Blocos</th>
                  <th className="cv-dir">SKUs</th>
                  <th className="cv-dir">Pessoas</th>
                  <th className="cv-dir">
                    Concluído
                    <Ajuda rotulo="concluído">
                      Quanto dos produtos daquele dia terminou o Giro (contado e reposto).
                    </Ajuda>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.dias.map((x) => (
                  <tr
                    key={x.dia}
                    className="cv-clicavel"
                    tabIndex={0}
                    title="Abrir a operação deste dia"
                    onClick={() => (window.location.hash = `#/giro-operacao?dia=${x.dia}`)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && (window.location.hash = `#/giro-operacao?dia=${x.dia}`)
                    }
                  >
                    <td className="cv-forte">{diaCurto(x.dia)}</td>
                    <td className="cv-dir">{x.setores}</td>
                    <td className="cv-dir">{x.blocos}</td>
                    <td className="cv-dir">{x.skus}</td>
                    <td className="cv-dir">{x.equipe}</td>
                    <td className="cv-dir">
                      {x.conclusao === null || x.estado === 'cancelada'
                        ? '—'
                        : `${String(x.conclusao)}%`}
                    </td>
                    <td>
                      <span
                        className={`gr-st ${x.estado === 'concluida' ? 'gr-st-ok' : x.estado === 'em_andamento' ? 'gr-st-rep' : ''}`}
                      >
                        {ROTULO_DO_ESTADO[x.estado]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!d.dias.length && <p className="cv-vazio">Nenhuma operação ainda.</p>}
          </div>
        )}
      </div>
    </section>
  );
}
